import { DialogPanel } from '@headlessui/react';
import { XIcon } from '@phosphor-icons/react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, DialogModal, getColorMapping, ModalActions, ModalBody, Text } from '@components';
import { StatusType, Translation, VariantType } from '@enums';
import { cn } from '@utils';

type ConfirmButtonProps = {
  children: ReactNode;
  onClick: () => void;
};

type DomainFormProps = {
  title: ReactNode;
  content: ReactNode;
  open: boolean;
  hideIcon?: boolean;
  onClose: () => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  confirmButton?: ConfirmButtonProps;
  type?: StatusType;
  topTitle?: boolean;
  className?: string;
};

export const InformationModal = ({
  title,
  content,
  open,
  hideIcon = false,
  topTitle = false,
  confirmButton,
  className,
  size = 'md',
  onClose,
  type,
}: DomainFormProps) => {
  const { t: tButton } = useTranslation(Translation.Common, { keyPrefix: 'button' });
  const colorMapping = getColorMapping(type ?? StatusType.Info);

  if (!open) return;
  return (
    <DialogModal notForm={true} open={open} onClose={onClose} withoutPadding size={size}>
      <DialogPanel className={cn('gap-5 flex flex-col', className)}>
        <div className={`${topTitle ? 'border-b' : ''} flex items-center justify-between px-6 py-4`}>
          <div className="flex space-x-4 items-center">
            {!hideIcon && topTitle && colorMapping?.icon({ className: 'w-5 h-5 ' })}
            <div>{topTitle && title}</div>
          </div>
          <Button
            variant={VariantType.Transparent}
            className="p-2.5 rounded-full enabled:shadow-none"
            hoverEffect
            adornment={<XIcon className="size-4" onClick={onClose} />}
          />
        </div>
        <ModalBody notForm className="max-w-full px-6 pb-7">
          {!hideIcon && !topTitle && (
            <div
              className={`rotate-45 w-fit rounded p-1.5 ${colorMapping?.background.light} ${colorMapping?.text.main}`}
            >
              <div className="-rotate-45"> {colorMapping?.icon({ className: 'size-8' })}</div>
            </div>
          )}
          <div className="space-y-3 flex flex-col w-full">
            {hideIcon && !topTitle && (
              <Text $level={2} $customizeColor className="font-medium text-gray-700">
                {title}
              </Text>
            )}
            {typeof content === 'string' ? (
              <Text $level={4} className="font-normal text-sm leading-6">
                {content}
              </Text>
            ) : (
              content
            )}
          </div>
          {confirmButton && (
            <ModalActions>
              <Button variant={VariantType.Outlined} onClick={onClose} className="items-center ring-theme-primary-main">
                <Text $level={6} $customizeColor className="ellipsis-text">
                  {tButton('cancel')}
                </Text>
              </Button>

              <Button className="bg-indigo-500 text-white" {...confirmButton} />
            </ModalActions>
          )}
        </ModalBody>
      </DialogPanel>
    </DialogModal>
  );
};
